"use client";

import Link from "next/link";
import { useState } from "react";
import { Loader2, TriangleAlert, Mail, Lock, LogIn, ArrowRight } from "lucide-react";
import { useRouter } from "next/navigation";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const CustomSignInCard = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const router = useRouter();

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success) {
        // Force complete page reload - all roles go to /dashboard
        window.location.replace('/dashboard');
      } else {
        setError(data.error || '로그인 실패');
      }
    } catch (error) {
      setError('로그인 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="w-full animate-in fade-in duration-700 zoom-in-95">
      <div className="bg-white/50 backdrop-blur-2xl rounded-[32px] border border-white/60 shadow-xl shadow-slate-200/50 p-8 md:p-10 relative overflow-hidden group hover:bg-white/60 transition-colors">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-indigo-100/30 rounded-full blur-[80px] -z-10 group-hover:bg-indigo-100/50 transition-colors duration-700" />
        <div className="absolute -bottom-10 -left-10 w-[300px] h-[300px] bg-blue-100/30 rounded-full blur-[80px] -z-10 group-hover:bg-blue-100/50 transition-colors duration-700" />

        <div className="relative z-10 text-center mb-8">
          <h1 className="text-3xl font-black text-slate-800 tracking-tight mb-2">MODi STUDIO</h1>
        </div>

        {error && (
          <div className="mb-6 bg-rose-50/80 backdrop-blur-md border border-rose-100/60 p-4 rounded-2xl flex items-center gap-x-3 text-sm text-rose-600 shadow-sm animate-in slide-in-from-top-2">
            <TriangleAlert className="size-5 flex-shrink-0" />
            <p className="font-semibold">{error}</p>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-5 relative z-10 w-full mb-8">
          <div className="space-y-2 relative group/input">
            <label htmlFor="email" className="text-sm font-bold text-slate-700 ml-1">
              이메일 주소
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within/input:text-indigo-500 transition-colors" />
              <Input
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
                type="email"
                disabled={loading}
                required
                className="pl-12 h-14 bg-white/60 backdrop-blur-md border-white/80 shadow-sm rounded-2xl focus-visible:ring-indigo-500 focus-visible:border-transparent transition-all w-full text-[15px] font-medium placeholder:text-slate-400"
              />
            </div>
          </div>

          <div className="space-y-2 relative group/input">
            <label htmlFor="password" className="text-sm font-bold text-slate-700 ml-1">
              비밀번호
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within/input:text-indigo-500 transition-colors" />
              <Input
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호를 입력하세요"
                type="password"
                disabled={loading}
                required
                className="pl-12 h-14 bg-white/60 backdrop-blur-md border-white/80 shadow-sm rounded-2xl focus-visible:ring-indigo-500 focus-visible:border-transparent transition-all w-full text-[15px] font-medium placeholder:text-slate-400"
              />
            </div>
            <p className="text-xs text-slate-500 ml-1 mt-1.5 leading-relaxed">
              비밀번호를 여러 번 잘못 입력할 경우 로그인 시도가 제한됩니다.
            </p>
          </div>

          <div className="flex items-center justify-between pt-2">
            {/* <div className="flex items-center pl-1 group/checkbox cursor-pointer">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="w-4 h-4 text-indigo-500 bg-white/60 border-slate-300 rounded focus:ring-indigo-500 transition-colors cursor-pointer"
              />
              <label htmlFor="remember-me" className="ml-2.5 block text-sm font-semibold text-slate-500 group-hover/checkbox:text-slate-700 transition-colors cursor-pointer">
                로그인 상태 유지
              </label>
            </div> */}
            {/* <Link href="/forgot-password" className="text-sm font-bold text-indigo-500 hover:text-indigo-600 transition-colors">
              비밀번호 찾기
            </Link> */}
          </div>

          <Button
            className="w-full h-14 rounded-2xl bg-slate-800 hover:bg-slate-900 text-white font-black shadow-lg shadow-slate-200/50 transition-all active:scale-[0.98] text-[15px] mt-2 group/btn"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="mr-2 w-5 h-5 animate-spin" />
            ) : (
              <span className="flex items-center justify-center gap-2">
                로그인 진행하기
                <ArrowRight className="w-5 h-5 opacity-70 group-hover/btn:translate-x-1 transition-transform" />
              </span>
            )}
          </Button>
        </form>

        {/* <div className="relative z-10 text-center border-t border-slate-200/60 pt-6">
          <p className="text-sm font-medium text-slate-500">
            아직 계정이 없으신가요?{" "}
            <Link href="/sign-up" className="font-bold text-indigo-600 hover:text-indigo-700 transition-colors px-1 py-0.5 rounded-md hover:bg-indigo-50">
              무료로 가입하기
            </Link>
          </p>
        </div> */}
      </div>
    </div>
  );
};
